import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Table, Tag, Typography, PLATFORM_THEME_COLORS } from '@thinknet-gmbh/platform-common-ui';
import { VacancyPatternStatusesEnum } from '@thinknet-gmbh/platform-common';

import { IVacanciesTableProps } from 'src/pages/vacancies/vacancies.interfaces';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { VACANCIES_PAGES } from 'src/pages/vacancies/vacancies.constants';
import { VACANCY_DETAILS_TABS } from 'src/pages/vacancies/sub-pages/vacancy-details/vacancy-details.constants';

import { VACANCIES_STATUS_STYLES } from './vacancies-list-table.constants';
import VacanciesTableRowPopover from './vacancies-table-row-popover.component';
import { TagWrapper } from './vacancies-list-table.styles';

const VacanciesListTable = ({
  dataSource,
  paginationConfig,
  handleModals,
}: IVacanciesTableProps) => {
  const { t } = useTranslation(['common', 'vacancies']);
  const navigate = useNavigate();

  const navigateToDetailsPage = (id: string) =>
    navigate(`${ROUTES.vacancies}/${VACANCIES_PAGES.vacancyDetails}&id=${id}`);

  const navigateToApplicantsPage = (id: string) =>
    navigate(
      `${ROUTES.vacancies}/${VACANCIES_PAGES.vacancyDetails}&id=${id}?type=${VACANCY_DETAILS_TABS.applicants}`
    );

  return (
    <Table
      t={t}
      dataSource={dataSource}
      pagination={paginationConfig}
      rowKey={record => record._id}
      onRow={record => ({
        onClick: () => navigateToDetailsPage(record._id),
      })}
    >
      <Table.Column
        title={t('common:labels.jobTitle')}
        dataIndex="job"
        key="job"
        render={job => <Typography>{job.title}</Typography>}
      />
      <Table.Column
        title={t('vacancies:labels.activeApplicant')}
        render={({ activeApplicant, _id }) => (
          <TagWrapper>
            <Typography
              onClick={e => {
                e.stopPropagation();
                navigateToApplicantsPage(_id);
              }}
            >
              {activeApplicant ? `${activeApplicant} ${t('common:statuses.active')}` : '-'}
            </Typography>
          </TagWrapper>
        )}
      />
      <Table.Column
        title={t('vacancies:labels.newApplicant')}
        render={({ _id, applicantsCount }) => (
          <TagWrapper>
            <Tag
              onClick={e => {
                e.stopPropagation();
                navigateToApplicantsPage(_id);
              }}
              background={PLATFORM_THEME_COLORS.tone.lightBlue}
              fontColor={PLATFORM_THEME_COLORS.primary.blue}
            >
              {applicantsCount
                ? `${applicantsCount} ${t('vacancies:labels.applicantsStatuses.new')}`
                : '-'}
            </Tag>
          </TagWrapper>
        )}
      />
      <Table.Column
        title={t('employee:labels.company')}
        dataIndex="company"
        key="company"
        render={company => <Typography>{company.name}</Typography>}
      />
      <Table.Column
        title={t('common:labels.location')}
        dataIndex={['jobLocation', 'companyLocation']}
        key="jobLocation"
        render={location => <Typography>{location.name}</Typography>}
      />
      <Table.Column
        title={t('common:labels.status')}
        dataIndex="status"
        key="status"
        render={status => {
          const { background, fontColor } =
            VACANCIES_STATUS_STYLES[status as VacancyPatternStatusesEnum];

          return (
            <Tag background={background} fontColor={fontColor}>
              {t(`vacancies:labels.statuses.${status.toLowerCase()}`).toLowerCase()}
            </Tag>
          );
        }}
      />
      <Table.Column
        title={t('common:labels.creationDate')}
        dataIndex="createdAt"
        key="createdAt"
        render={createdAt => <Typography>{moment(createdAt).format('DD.MM.YYYY')}</Typography>}
      />
      <Table.Column
        width={50}
        title={t('common:labels.action')}
        render={record => (
          <VacanciesTableRowPopover
            handleModals={handleModals}
            record={record}
            t={t}
            navigate={navigateToDetailsPage}
          />
        )}
      />
    </Table>
  );
};

export default VacanciesListTable;
